import React from "react";
import { Crown, Star, Sparkles, ShieldCheck, BadgeCheck } from "lucide-react";

/**
 * Small, subtle status pill. Not loud. Reads premium.
 * 
 * Usage:
 *   <BadgePill badge="inner_circle" />
 *   <BadgePill text="Custom" color="blue" icon={<Star />} />
 */

const BADGE_STYLES = {
  inner_circle: {
    text: "Inner Circle",
    bg: "bg-yellow-500/10",
    border: "border-yellow-500/30",
    textColor: "text-yellow-200",
    Icon: Crown,
  },
  elite: {
    text: "Elite",
    bg: "bg-purple-500/10",
    border: "border-purple-500/30",
    textColor: "text-purple-200",
    Icon: Star,
  },
  elite_plus: {
    text: "Elite+",
    bg: "bg-pink-500/10",
    border: "border-pink-500/30",
    textColor: "text-pink-200",
    Icon: Sparkles,
  },
  verified: {
    text: "Verified",
    bg: "bg-blue-500/10",
    border: "border-blue-500/30",
    textColor: "text-blue-200",
    Icon: BadgeCheck,
  },
  admin: {
    text: "Admin",
    bg: "bg-red-500/10",
    border: "border-red-500/30",
    textColor: "text-red-200",
    Icon: ShieldCheck,
  },
};

export default function BadgePill({ 
  badge, 
  text, 
  color = "yellow",
  icon,
  size = "sm",
  className = "" 
}) {
  // Get style from preset or build custom
  const style = badge ? BADGE_STYLES[badge] : null;
  
  const displayText = text || style?.text || badge;
  const Icon = icon || style?.Icon || Crown;
  
  const bgClass = style?.bg || `bg-${color}-500/10`;
  const borderClass = style?.border || `border-${color}-500/30`;
  const textClass = style?.textColor || `text-${color}-200`;
  
  const sizeClasses = size === "xs" 
    ? "px-1.5 py-0.5 text-[10px] gap-0.5" 
    : "px-2 py-0.5 text-xs gap-1";
  const iconSize = size === "xs" ? 10 : 12;

  return (
    <span
      className={`inline-flex items-center rounded-full border font-semibold ${bgClass} ${borderClass} ${textClass} ${sizeClasses} ${className}`}
    >
      <Icon size={iconSize} />
      {displayText}
    </span>
  );
}

/**
 * Render multiple badges for a user
 */
export function UserBadges({ user, size = "sm", className = "" }) {
  if (!user) return null;
  
  const badges = [];
  
  // Check for Inner Circle
  const ent = user.entitlements || {};
  const ic = ent.inner_circle;
  if (user.is_inner_circle || ic === true || (ic && ic.active)) {
    badges.push("inner_circle");
  }
  
  // Check for Elite+ (show instead of Elite if both)
  const ep = ent.elite_plus;
  if (ep === true || (ep && ep.active)) {
    badges.push("elite_plus");
  } else {
    // Check for Elite
    const elite = ent.elite;
    if (elite === true || (elite && elite.active) || ent.elite_active) {
      badges.push("elite");
    }
  }
  
  // Check verified
  if (user.identity_verified || user.is_verified) {
    badges.push("verified");
  }
  
  // Check badges array as fallback
  if (user.badges && Array.isArray(user.badges)) {
    user.badges.forEach((b) => {
      const key = typeof b === "string" ? b : b.key || b.name;
      if (key && !badges.includes(key) && BADGE_STYLES[key]) {
        badges.push(key);
      }
    });
  }
  
  if (badges.length === 0) return null;
  
  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {badges.map((badge) => (
        <BadgePill key={badge} badge={badge} size={size} />
      ))}
    </div>
  );
}
